/**
 * 认证状态管理 Store
 * 
 * 管理用户登录状态、会话信息和认证相关操作
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/api'
import type { User, LoginRequest, ChangePasswordRequest, AuthErrorCode } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  // ========== State ==========
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const error = ref<{ code: AuthErrorCode | string; message: string } | null>(null)

  // ========== Getters ==========
  const isAuthenticated = computed(() => user.value !== null)
  const currentUser = computed(() => user.value)
  const username = computed(() => user.value?.display_name || user.value?.username || '')

  // ========== Actions ==========

  /** 登录 */
  async function login(credentials: LoginRequest): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await authApi.login(credentials)
      user.value = response.user
      return true
    } catch (e: unknown) {
      const err = e as { code: string; message: string }
      error.value = { code: err.code, message: err.message }
      return false
    } finally {
      isLoading.value = false
    }
  }

  /** 登出 */
  async function logout(): Promise<void> {
    try {
      await authApi.logout()
    } finally {
      user.value = null
    }
  }

  /** 获取当前用户信息 */
  async function fetchCurrentUser(): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      user.value = await authApi.me()
      return true
    } catch (e: unknown) {
      const err = e as { code: string; message: string }
      error.value = { code: err.code, message: err.message }
      user.value = null
      return false
    } finally {
      isLoading.value = false
    }
  }

  /** 修改密码 */
  async function changePassword(data: ChangePasswordRequest): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      await authApi.changePassword(data)
      return true
    } catch (e: unknown) {
      const err = e as { code: string; message: string }
      error.value = { code: err.code, message: err.message }
      return false
    } finally {
      isLoading.value = false
    }
  }

  /** 清除错误 */
  function clearError(): void {
    error.value = null
  }

  /** 重置状态（用于测试或特殊情况） */
  function reset(): void {
    user.value = null
    isLoading.value = false
    error.value = null
  }

  return {
    // State
    user,
    isLoading,
    error,
    // Getters
    isAuthenticated,
    currentUser,
    username,
    // Actions
    login,
    logout,
    fetchCurrentUser,
    changePassword,
    clearError,
    reset
  }
})
